import { flushSync } from 'react-dom';
import gsap from 'gsap';
import { Flip } from 'gsap/Flip';
import { prefersReducedMotion } from '../theme/themeTransition';

let isFlipRegistered = false;

export function ensureStudioGsapPlugins(): void {
  if (!isFlipRegistered && typeof window !== 'undefined') {
    gsap.registerPlugin(Flip);
    isFlipRegistered = true;
  }
}

export interface StudioSidebarToggleOptions {
  side: 'left' | 'right';
  nextOpen: boolean;
  layoutRoot: HTMLElement | null;
  triggerButton?: HTMLElement | null;
  applyState: () => void;
}

/**
 * Orchestrates opening and closing of the Studio Left and Right sidebars using
 * GSAP Flip for layout transitions and staggered inner-section reveal.
 */
export function runStudioSidebarToggle({
  side,
  nextOpen,
  layoutRoot,
  triggerButton,
  applyState,
}: StudioSidebarToggleOptions): void {
  if (typeof window === 'undefined' || !layoutRoot || prefersReducedMotion()) {
    applyState();
    return;
  }

  ensureStudioGsapPlugins();

  if (triggerButton) {
    gsap.fromTo(
      triggerButton,
      { scale: 0.88 },
      {
        scale: 1,
        duration: 0.32,
        ease: 'back.out(2.2)',
         overwrite: 'auto',
        clearProps: 'transform',
      }
    );
  }

  const sidebarSelector = side === 'left' ? '.studio-sidebar-left' : '.studio-sidebar-right';
  const sidebarEl = layoutRoot.querySelector<HTMLElement>(sidebarSelector);
  const canvasStageEl = layoutRoot.querySelector<HTMLElement>('.studio-main-canvas .canvas-stage');

  let flipState: Flip.FlipState | null = null;
  try {
    const targets = [sidebarEl, canvasStageEl].filter(Boolean) as HTMLElement[];
    if (targets.length > 0) {
      flipState = Flip.getState(targets);
    }
  } catch {
    flipState = null;
  }

  flushSync(() => {
    applyState();
  });

  if (flipState) {
    try {
      Flip.from(flipState, {
        duration: 0.36,
        ease: 'power3.out',
        absolute: false,
        overwrite: 'auto',
        clearProps: 'transform',
      });
    } catch {
      // Fallback safely if Flip cannot measure in headless environments
    }
  }

  const updatedSidebar = layoutRoot.querySelector<HTMLElement>(sidebarSelector);
  if (nextOpen && updatedSidebar) {
    const offsetX = side === 'left' ? -18 : 18;
    gsap.fromTo(
      updatedSidebar,
      { x: offsetX, autoAlpha: 0.4 },
      {
        x: 0,
        autoAlpha: 1,
        duration: 0.34,
        ease: 'power3.out',
        overwrite: 'auto',
        clearProps: 'transform,opacity,visibility',
      }
    );

    const innerSections = Array.from(updatedSidebar.children).filter(
      (child): child is HTMLElement =>
        child instanceof HTMLElement && !child.classList.contains('mobile-drawer-header')
    );

    if (innerSections.length > 0) {
      gsap.fromTo(
        innerSections,
        { x: side === 'left' ? -12 : 12, autoAlpha: 0 },
        {
          x: 0,
          autoAlpha: 1,
          duration: 0.32,
          stagger: 0.04,
          ease: 'power3.out',
          overwrite: 'auto',
          clearProps: 'transform,opacity,visibility',
        }
      );
    }
  } else if (!nextOpen && canvasStageEl) {
    gsap.fromTo(
      canvasStageEl,
      { scale: 0.992 },
      {
        scale: 1,
        duration: 0.3,
        ease: 'power2.out',
        overwrite: 'auto',
        clearProps: 'transform',
      }
    );
  }
}

export interface VectorCompletionRevealOptions {
  canvasRoot: HTMLElement | null;
  isSplitView?: boolean;
  onUpdateSplitPosition?: (position: number) => void;
}

/**
 * Runs the "Pixel-to-Path" completion sweep on the Studio canvas when vectorization
 * finishes, sweeping the SVG container clip-path and split comparison slider.
 */
export function animateVectorCompletionReveal({
  canvasRoot,
  isSplitView = false,
  onUpdateSplitPosition,
}: VectorCompletionRevealOptions): () => void {
  if (!canvasRoot || typeof window === 'undefined' || prefersReducedMotion()) {
    return () => {};
  }

  const ctx = gsap.context(() => {
    const svgContainers = gsap.utils.toArray<HTMLElement>('.vector-svg-container', canvasRoot);
    const qualityBadge = canvasRoot.querySelector<HTMLElement>('.quality-report-trigger');

    if (svgContainers.length > 0) {
      gsap.fromTo(
        svgContainers,
        {
          clipPath: 'inset(0 100% 0 0)',
          scale: 0.975,
          autoAlpha: 0.25,
        },
        {
          clipPath: 'inset(0 0% 0 0)',
          scale: 1,
          autoAlpha: 1,
          duration: 0.62,
          ease: 'power3.out',
          overwrite: 'auto',
          clearProps: 'clipPath,transform,opacity,visibility',
        }
      );
    }

    if (isSplitView && onUpdateSplitPosition) {
      const splitProxy = { pos: 84 };
      onUpdateSplitPosition(splitProxy.pos);
      gsap.to(splitProxy, {
        pos: 50,
        duration: 0.72,
        ease: 'power3.out',
        onUpdate: () => {
          onUpdateSplitPosition(splitProxy.pos);
        },
      });
    }

    if (qualityBadge) {
      gsap.fromTo(
        qualityBadge,
        { y: 12, autoAlpha: 0 },
        {
          y: 0,
          autoAlpha: 1,
          duration: 0.42,
          delay: 0.12,
          ease: 'back.out(1.7)',
          overwrite: 'auto',
          clearProps: 'transform,opacity,visibility',
        }
      );
    }
  }, canvasRoot);

  return () => {
    ctx.revert();
  };
}

/**
 * Staggers AI metadata keyword chips and validation status card inside MetadataEditorPanel.
 */
export function animateMetadataKeywordsStagger(panelRoot: HTMLElement | null): () => void {
  if (!panelRoot || typeof window === 'undefined' || prefersReducedMotion()) {
    return () => {};
  }

  const ctx = gsap.context(() => {
    const chips = gsap.utils.toArray<HTMLElement>('.metadata-keyword-chips span', panelRoot);
    const validationCard = panelRoot.querySelector<HTMLElement>('.metadata-validation-card');

    if (validationCard) {
      gsap.fromTo(
        validationCard,
        { y: 8, autoAlpha: 0 },
        {
          y: 0,
          autoAlpha: 1,
          duration: 0.34,
          ease: 'power2.out',
          overwrite: 'auto',
          clearProps: 'transform,opacity,visibility',
        }
      );
    }

    if (chips.length > 0) {
      gsap.fromTo(
        chips,
        { scale: 0.86, y: 6, autoAlpha: 0 },
        {
          scale: 1,
          y: 0,
          autoAlpha: 1,
          duration: 0.28,
          stagger: 0.016,
          ease: 'back.out(1.8)',
          overwrite: 'auto',
          clearProps: 'transform,opacity,visibility',
        }
      );
    }
  }, panelRoot);

  return () => {
    ctx.revert();
  };
}

/**
 * Tweens the Quality Report readiness score, category progress bars (scaleX),
 * and check category cards when QualityReportDrawer opens.
 */
export function animateQualityReportDrawer(
  drawerRoot: HTMLElement | null,
  targetScore: number
): () => void {
  if (!drawerRoot || typeof window === 'undefined') {
    return () => {};
  }

  const scoreEl = drawerRoot.querySelector<HTMLElement>('[data-gsap-quality-score]');
  if (prefersReducedMotion()) {
    if (scoreEl) scoreEl.textContent = String(Math.round(targetScore));
    return () => {};
  }

  const ctx = gsap.context(() => {
    if (scoreEl) {
      const counter = { value: 0 };
      gsap.to(counter, {
        value: targetScore,
        duration: 0.65,
        ease: 'power3.out',
        onUpdate: () => {
          scoreEl.textContent = String(Math.round(counter.value));
        },
        onComplete: () => {
          scoreEl.textContent = String(Math.round(targetScore));
        },
      });
    }

    const barSpans = gsap.utils.toArray<HTMLElement>(
      '.quality-category-scores i > span',
      drawerRoot
    );
    if (barSpans.length > 0) {
      gsap.fromTo(
        barSpans,
        { scaleX: 0, transformOrigin: 'left center' },
        {
          scaleX: 1,
          duration: 0.58,
          stagger: 0.06,
          ease: 'power3.out',
          clearProps: 'transform',
        }
      );
    }

    const checkCategories = gsap.utils.toArray<HTMLElement>('.quality-category', drawerRoot);
    if (checkCategories.length > 0) {
      gsap.fromTo(
        checkCategories,
        { y: 12, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.38,
          stagger: 0.05,
          ease: 'power2.out',
          clearProps: 'transform,opacity',
        }
      );
    }
  }, drawerRoot);

  return () => {
    ctx.revert();
  };
}

